<script lang="ts">
    import type {Document} from 'typings/portaro.be.types';
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {TabButton, TabId, TabPageDefinitionsMap} from 'shared/ui-widgets/tabset/types';
    import type {OptimisationSumsResponse} from './types';
    import {RecordGridService} from 'src/features/record-grid/services/record-grid.service';
    import {getCurrentLanguage, getInjector} from 'core/svelte-context/context';
    import {onDestroy, onMount} from 'svelte';
    import {createProjectReportOptimisationContext} from './project-report-optimisation-context';
    import {ProjectReportOptimisationService} from './project-report-optimisation.service';
    import {priceFormatter} from 'shared/utils/pipes';
    import {pipe} from 'core/utils';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {fly} from 'svelte/transition';
    import {LabeledValue} from 'src/features/erp-sutor/shared';
    import {SUTOR_REPORTS_OPTIMISATION_PAGE} from 'src/features/erp-sutor/sutor-constants';
    import {ErpTabbedSubpagesContainer, TabbedSubpagesRouter} from 'src/features/erp/components';
    import ReportOptimisationEditationTab from './editation/ReportOptimisationEditationTab.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import ReportOptimisationOverviewsTab from './overviews/ReportOptimisationOverviewsTab.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpLoadableContainer from 'shared/layouts/containers/KpLoadableContainer.svelte';
    import KpVerticalSeparator from 'shared/ui-widgets/separator/KpVerticalSeparator.svelte';

    export let record: Document;

    const recordGridService = getInjector().getByClass(RecordGridService);
    const service = getInjector().getByClass(ProjectReportOptimisationService);
    const context = createProjectReportOptimisationContext(record, service);
    const priceFormat = priceFormatter(getCurrentLanguage());

    let loading = true;

    let optimisedBusinessItemRecord: RecordRow | null = null;
    let overviewedBusinessItemRecord: RecordRow | null = null;
    let activeTab: TabId = 'tab-editation';
    let tabToSelect: TabId | null = null;

    let optimisationSums: OptimisationSumsResponse | null = null;

    const optimisedBusinessItemRecordUnsubscribe = context.optimisedBusinessItemRecord$.subscribe((currentOptimisedBusinessItemRecord) => optimisedBusinessItemRecord = currentOptimisedBusinessItemRecord);
    // When the signal is set, we need to switch to the overview tab
    const openOverviewSignalUnsubscribe = context.openOverviewSignal$.subscribe((businessItemRecord) => {
        overviewedBusinessItemRecord = businessItemRecord;

        if (exists(businessItemRecord)) {
            tabToSelect = 'tab-overviews';
        }
    });

    // Tabs setup
    const tabs: TabPageDefinitionsMap = {};
    const tabButtons: TabButton[] = [
        {
            id: 'tab-editation',
            label: 'Náklady k optimalizaci',
            icon: 'list',
            tabPageWithoutPadding: true
        },
        {
            id: 'tab-overviews',
            label: 'Přehledy',
            icon: 'info',
            tabPageWithoutPadding: true
        }
    ];

    onMount(async () => {
        const optimisationFond = await recordGridService.getFond(SUTOR_REPORTS_OPTIMISATION_PAGE.fond);

        // We don't want to wait for this - just let it load in the background
        service.getReportSums(record).then((response) => optimisationSums = response);

        tabs['tab-overviews'] = {component: ReportOptimisationOverviewsTab};
        tabs['tab-editation'] = {
            component: ReportOptimisationEditationTab,
            props: {
                fond: optimisationFond
            }
        };

        loading = false;
    });

    onDestroy(() => {
        cleanup(optimisedBusinessItemRecordUnsubscribe, openOverviewSignalUnsubscribe);
    });

    const handleUnselectOptimisedBusinessItem = () => {
        context.setOptimisedBusinessItemRecord(null);
    };

    const handleUnselectOverviewedBusinessItem = () => {
        context.setOpenOverviewSignal(null);
        tabToSelect = 'tab-editation';
    };

    const handleTabChange = (event: CustomEvent<TabId>) => {
        if (event.detail === 'tab-editation') {
            context.setOptimisedBusinessItemRecord(null);
            context.setOpenOverviewSignal(null);
        }
    };
</script>

<KpLoadableContainer fillAvailableSpace {loading}>
    <ErpTabbedSubpagesContainer {tabButtons}
                                bind:activeTab
                                bind:tabToSelect
                                on:tab-change={handleTabChange}
                                additionalClasses="reports-optimisation-tabbed-subpages"
                                withoutUrlManagement>

        <svelte:fragment slot="additional-content">
            {#if activeTab === 'tab-overviews' && exists(overviewedBusinessItemRecord)}
                <KpVerticalSeparator height="16px"/>

                <div class="additional-content-container" in:fly={{x: 10, duration: 250}}>
                    <KpButton buttonStyle="brand-orange-new" on:click={handleUnselectOverviewedBusinessItem}>
                        <IconedContent icon="arrow-small-left">Zpět na tabulku</IconedContent>
                    </KpButton>
                </div>
            {/if}

            {#if activeTab === 'tab-editation'}
                <KpVerticalSeparator height="16px"/>

                {#if exists(optimisedBusinessItemRecord)}
                    <div class="additional-content-container" in:fly={{x: 10, duration: 250}}>
                        <KpButton buttonStyle="brand-orange-new" on:click={handleUnselectOptimisedBusinessItem}>
                            <IconedContent icon="cross-circle">Zavřít optimalizaci</IconedContent>
                        </KpButton>

                        <LabeledValue label="Optimalizovaná položka">{optimisedBusinessItemRecord.name}</LabeledValue>
                    </div>
                {:else}
                    <div class="additional-content-container" in:fly={{x: 10, duration: 250}}>
                        <small class="text-muted">Celkové součty:</small>

                        {#if !exists(optimisationSums)}
                            <KpLoadingBlock size="xs"/>
                        {:else}
                            <LabeledValue label="Vykázáno">{pipe(optimisationSums.reportedPrice, priceFormat)}</LabeledValue>
                            <LabeledValue label="Účtováno">{pipe(optimisationSums.chargedPrice, priceFormat)}</LabeledValue>
                        {/if}
                    </div>
                {/if}
            {/if}
        </svelte:fragment>

        <TabbedSubpagesRouter {tabs} {activeTab}/>
    </ErpTabbedSubpagesContainer>
</KpLoadableContainer>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .additional-content-container {
        display: flex;
        align-items: center;
        gap: @spacing-l;

        small {
            white-space: nowrap;
        }
    }
</style>